import { Aperture, Camera, Move3d, RotateCcw, SunMedium, Wand2 } from 'lucide-react';
import { CineCombo } from './CineCombo';
import { cn } from '../../lib/utils';

const SECTIONS = [
  { id: 'direction', title: 'Camera direction', Icon: Move3d, keys: ['camera_movement', 'shot_size', 'camera_angle', 'camera_rig', 'transition'] },
  { id: 'camera', title: 'Camera', Icon: Camera, keys: ['camera_brand', 'camera_type', 'camera_body', 'sensor_format'] },
  { id: 'lens', title: 'Lens', Icon: Aperture, keys: ['lens_type', 'lens', 'focal_length', 'aperture', 'depth_of_field', 'filter'] },
  { id: 'look', title: 'Look & light', Icon: SunMedium, keys: ['film_look', 'lighting', 'color_grade', 'frame_rate', 'shutter_angle'] },
];

const LABELS = {
  camera_movement: 'Camera direction', shot_size: 'Shot size', camera_angle: 'Camera angle', camera_rig: 'Rig / support',
  transition: 'Transition out', camera_brand: 'Camera brand', camera_type: 'Camera type', camera_body: 'Camera body',
  sensor_format: 'Sensor / format', lens_type: 'Lens type', lens: 'Lens brand & series', focal_length: 'Focal length',
  aperture: 'Aperture', depth_of_field: 'Depth of field', filter: 'Filter / diffusion', film_look: 'Film stock / look',
  lighting: 'Lighting', color_grade: 'Color grade', frame_rate: 'Frame rate', shutter_angle: 'Shutter angle',
};

const groupBy = (list, key) => Object.entries(
  list.reduce((acc, x) => ({ ...acc, [x[key]]: [...(acc[x[key]] || []), x.label] }), {}),
).map(([heading, items]) => ({ heading, items }));

export function CinematographyPanel({ catalog, value = {}, onChange, testPrefix = 'cine', showPresets = true }) {
  const { camera_bodies: bodies, lenses } = catalog;

  const optionsFor = (k) => {
    if (k === 'camera_brand') return { options: catalog.camera_brands };
    if (k === 'camera_body') {
      return { groups: groupBy(bodies.filter((b) => (!value.camera_brand || b.brand === value.camera_brand)
        && (!value.camera_type || b.type === value.camera_type)), 'brand') };
    }
    if (k === 'lens') return { groups: groupBy(lenses.filter((l) => !value.lens_type || l.type === value.lens_type), 'brand') };
    return { options: catalog.options[k] || [] };
  };

  const set = (k, v) => {
    const next = { ...value, [k]: v };
    if (k === 'camera_body') {
      const b = bodies.find((x) => x.label === v);
      if (b) Object.assign(next, { camera_brand: b.brand, camera_type: b.type, sensor_format: b.format });
    }
    if (k === 'camera_brand' && bodies.find((x) => x.label === value.camera_body)?.brand !== v) next.camera_body = '';
    if (k === 'lens') { const l = lenses.find((x) => x.label === v); if (l) next.lens_type = l.type; }
    onChange(next);
  };

  return (
    <div className="space-y-5" data-testid={`${testPrefix}-panel`}>
      {showPresets && (
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300/80">
            <Wand2 size={13} /> One-tap visual style
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {catalog.style_presets.map((p) => (
              <button
                key={p.id}
                type="button"
                title={p.blurb}
                onClick={() => onChange({ ...value, ...p.settings, style_preset: p.id })}
                data-testid={`${testPrefix}-preset-${p.id}`}
                className={cn(
                  'shrink-0 rounded-full border px-3 py-1.5 text-xs transition-colors',
                  value.style_preset === p.id
                    ? 'border-amber-400 bg-amber-500/15 text-amber-100'
                    : 'border-neutral-800 bg-neutral-900/60 text-neutral-300 hover:border-neutral-600',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {SECTIONS.map(({ id, title, Icon, keys }) => (
        <div key={id}>
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">
            <Icon size={13} className="text-amber-300/80" /> {title}
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
            {keys.map((k) => (
              <CineCombo key={k} label={LABELS[k]} value={value[k] || ''} onChange={(v) => set(k, v)}
                testId={`${testPrefix}-${k.replace(/_/g, '-')}`} {...optionsFor(k)} />
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange({})} data-testid={`${testPrefix}-reset`}
        className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-200">
        <RotateCcw size={12} /> Reset all to AI-decides
      </button>
    </div>
  );
}
